/**
 * VehiclesSettingsTab - Aba "Veículos" das Configurações do tenant
 *
 * Responsável por habilitar/desabilitar o módulo de veículos e configurar a
 * integração de consulta por placa (dados do veículo e multas em tempo real).
 * O cadastro de categorias foi movido para a página exclusiva
 * "Veículos › Categorias Veículos" (menu lateral).
 *
 * @version 3.0.0
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 * RELEASE: 2026-08-08 v2.0.0 - Remoção do cadastro de categorias (agora em página própria)
 * RELEASE: 2026-08-08 v3.0.0 - Configuração da consulta por placa, multas e UF padrão
 * RELEASE: 2026-08-08 v3.1.0 - Validade configurável do cache de consulta por placa
 * RELEASE: 2026-08-24 v3.2.0 - Nova opção "Restringir seleção aos veículos do cliente"
 */

import { useEffect, useState } from 'react';
import { Car, Info, ScanSearch, TriangleAlert, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  SettingsCard,
  SettingsInfoBanner,
  SettingsSectionHeader,
} from '@/components/tenant/SettingsInfoRow';
import { useVehiclesModule } from '@/hooks/useVehiclesModule';
import { InlineNotice } from '@/components/ui/inline-notice';

/** Unidades da federação para consulta de multas */
const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

/** Aba de configurações do módulo de veículos */
export function VehiclesSettingsTab() {
  const {
    isEnabled,
    isLoading,
    toggleModule,
    plateLookupEnabled,
    finesEnabled,
    finesUf,
    plateCacheTtlDays,
    scopeToCustomer,
    updateLookupSettings,
  } = useVehiclesModule();
  const navigate = useNavigate();

  // Estado local da UF (salva ao alterar)
  const [uf, setUf] = useState<string>(finesUf ?? '');
  useEffect(() => setUf(finesUf ?? ''), [finesUf]);

  // Estado local do TTL do cache de placa (salva ao sair do campo)
  const [ttlDays, setTtlDays] = useState<string>(String(plateCacheTtlDays ?? 90));
  useEffect(() => setTtlDays(String(plateCacheTtlDays ?? 90)), [plateCacheTtlDays]);

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-lg" />;
  }


  return (
    <div className="space-y-6">
      {/* Ativação do módulo */}
      <SettingsCard>
        <SettingsSectionHeader
          eyebrow="Módulo"
          title="Veículos"
          description="Habilita o menu lateral Veículos e os campos de veículos em clientes, negócios e contratos."
        />
        <div className="flex items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-start gap-3">
            <Car className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="space-y-1 text-sm">
              <p className="font-medium text-foreground">Habilitar módulo de veículos</p>
              <p className="text-muted-foreground">
                Quando desligado, nenhuma tela, aba ou campo de veículo é exibido no sistema.
              </p>
            </div>
          </div>
          <Switch
            checked={isEnabled}
            disabled={toggleModule.isPending}
            onCheckedChange={(checked) => toggleModule.mutate(checked)}
          />
        </div>

        {/* v3.2.0: escopo de seleção de veículos (cliente/contato) */}
        <div className="flex items-center justify-between gap-4 border-t border-border/60 px-6 py-4">
          <div className="flex items-start gap-3">
            <Users className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="space-y-1 text-sm">
              <p className="font-medium text-foreground">
                Restringir seleção aos veículos do cliente
              </p>
              <p className="text-muted-foreground">
                Quando ligado, os seletores de veículo (negócios e tarefas) listam apenas os
                veículos vinculados ao cliente/contato do registro. Indicado para rastreamento
                veicular. Desligado, a busca continua em toda a base do tenant (ex.: locadoras).
              </p>
            </div>
          </div>
          <Switch
            checked={scopeToCustomer}
            disabled={!isEnabled || updateLookupSettings.isPending}
            onCheckedChange={(checked) =>
              updateLookupSettings.mutate({ scopeToCustomer: checked })
            }
          />
        </div>

        <div className="space-y-3 px-6 pb-5">
          <SettingsInfoBanner icon={Info}>
            Carros, motos e caminhões usam a base oficial FIPE. As categorias do tipo "Outros" são
            mantidas na página exclusiva do módulo.
          </SettingsInfoBanner>
          <Button
            variant="outline"
            size="sm"
            disabled={!isEnabled}
            onClick={() => navigate('/tenant/vehicles/categories')}
          >
            Gerenciar categorias de veículo
          </Button>
        </div>
      </SettingsCard>

      {/* ============ Consulta por placa (integração externa) ============ */}
      <SettingsCard>
        <SettingsSectionHeader
          eyebrow="Integração"
          title="Consulta por placa"
          description="Preenche automaticamente os dados do veículo a partir da placa e consulta multas em tempo real."
        />

        <div className="flex items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-start gap-3">
            <ScanSearch className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="space-y-1 text-sm">
              <p className="font-medium text-foreground">Habilitar consulta por placa</p>
              <p className="text-muted-foreground">
                No cadastro de veículo, o botão "Buscar" preenche marca, modelo, ano, cor, chassi e
                dados FIPE automaticamente a partir da placa. Provedor ativo: API Placas.
              </p>

            </div>
          </div>
          <Switch
            checked={plateLookupEnabled}
            disabled={!isEnabled || updateLookupSettings.isPending}
            onCheckedChange={(checked) =>
              updateLookupSettings.mutate({ plateLookupEnabled: checked })
            }
          />
        </div>

        {/* v3.2.0 — aviso explícito de que o botão "Buscar" some do cadastro
            enquanto a consulta por placa estiver desativada */}
        {isEnabled && !plateLookupEnabled && (
          <div className="px-6 pb-4">
            <InlineNotice variant="warning">
              Enquanto esta opção estiver desativada, o botão "Buscar" não aparece no modal de
              cadastro de veículo e o preenchimento automático pela placa fica indisponível.
            </InlineNotice>
          </div>
        )}


        <div className="flex items-center justify-between gap-4 border-t px-6 py-4">
          <div className="flex items-start gap-3">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="space-y-1 text-sm">
              <p className="font-medium text-foreground">Habilitar consulta de multas</p>
              <p className="text-muted-foreground">
                Exibe a aba "Multas" no veículo. A sincronização automática com o órgão de trânsito
                está em implementação e será liberada em fase própria.
              </p>

            </div>
          </div>
          <Switch
            checked={finesEnabled}
            disabled={!isEnabled || updateLookupSettings.isPending}
            onCheckedChange={(checked) => updateLookupSettings.mutate({ finesEnabled: checked })}
          />
        </div>

        <div className="space-y-3 border-t px-6 py-4">
          <div className="flex flex-wrap gap-4">
            <div className="w-full max-w-xs space-y-1.5">
              <Label className="text-xs font-medium uppercase text-muted-foreground">
                UF padrão para multas
              </Label>
              <Select
                value={uf || undefined}
                disabled={!isEnabled || !finesEnabled || updateLookupSettings.isPending}
                onValueChange={(value) => {
                  setUf(value);
                  updateLookupSettings.mutate({ finesUf: value });
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Selecione a UF" />
                </SelectTrigger>
                <SelectContent>
                  {UFS.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* v3.1.0 — validade do cache de consulta por placa (controle de custo) */}
            <div className="w-full max-w-[200px] space-y-1.5">
              <Label className="text-xs font-medium uppercase text-muted-foreground">
                Validade do cache (dias)
              </Label>
              <Input
                type="number"
                min={1}
                max={365}
                className="h-9"
                value={ttlDays}
                disabled={!isEnabled || !plateLookupEnabled || updateLookupSettings.isPending}
                onChange={(e) => setTtlDays(e.target.value)}
                onBlur={() => {
                  const parsed = Number(ttlDays);
                  // Só salva quando o valor mudou e está dentro dos limites aceitos pela RPC
                  if (!Number.isFinite(parsed) || parsed < 1 || parsed > 365) {
                    setTtlDays(String(plateCacheTtlDays));
                    return;
                  }
                  if (parsed !== plateCacheTtlDays) {
                    updateLookupSettings.mutate({ plateCacheTtlDays: parsed });
                  }
                }}
              />
            </div>
          </div>


          <SettingsInfoBanner icon={Info}>
            Cada consulta por placa ou de multas consome créditos do provedor contratado. Os dados
            cadastrais ficam em cache pelo período configurado acima; as multas são sempre consultadas ao vivo.
          </SettingsInfoBanner>
        </div>
      </SettingsCard>
    </div>
  );

}
